import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Phone, Mail, MapPin, Clock, ArrowRight, 
  Send, CheckCircle, Landmark, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    value: '+91 95413 77777',
    link: 'tel:+919541377777',
    description: 'Monday to Saturday, 9 AM - 6 PM IST'
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'info@nstuindia.com',
    link: 'mailto:info@nstuindia.com',
    description: 'We reply within 24 hours'
  },
  {
    icon: MapPin,
    title: 'Address',
    value: 'VPO Haluwas, Bhiwani',
    link: null,
    description: 'District Bhiwani, Haryana 127021'
  },
  {
    icon: Clock,
    title: 'Business Hours',
    value: 'Mon - Sat: 9 AM - 6 PM',
    link: null,
    description: 'Indian Standard Time (IST)'
  }
];

const subjectOptions = [
  'Government Project Inquiry',
  'Enterprise Solution Request',
  'Partnership Opportunity',
  'Support Request',
  'Career Inquiry',
  'General Inquiry'
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-nstu-green-800" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-nstu-gold-400 text-nstu-green-900 mb-6">Get In Touch</Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Let's Discuss Your Project
            </h1>
            <p className="text-gray-300 text-lg">
              Whether you're a government department planning a digital initiative or an enterprise 
              seeking technology transformation, we're here to help. Reach out to us and let's start 
              a conversation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info) => {
              const IconComponent = info.icon;
              return (
                <Card key={info.title} className="border-0 shadow-lg card-hover">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-nstu-green-100 flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-nstu-green-800" />
                    </div>
                    <h3 className="font-semibold text-nstu-green-800 mb-2">{info.title}</h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-lg font-bold text-nstu-gold-600 hover:text-nstu-gold-700 transition-colors"
                        data-testid={`contact-${info.title.toLowerCase()}-link`}
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-lg font-bold text-gray-800">{info.value}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-1">{info.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Side - Info */}
            <div>
              <Badge className="bg-nstu-green-100 text-nstu-green-800 mb-4">Why Contact Us?</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-6">
                We're Ready to Help
              </h2>
              <p className="text-gray-600 mb-8">
                Our team of experts is available to discuss your project requirements, provide 
                technical consultations, and guide you through the best solutions for your needs.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm border-l-4 border-nstu-gold-400">
                  <Landmark className="w-8 h-8 text-nstu-green-800 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-nstu-green-800 mb-1">Government Projects</h3>
                    <p className="text-gray-600 text-sm">
                      Specialized support for e-Governance initiatives, citizen services, and digital transformation projects
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm border-l-4 border-gray-300">
                  <Briefcase className="w-8 h-8 text-nstu-green-800 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-nstu-green-800 mb-1">Enterprise Solutions</h3>
                    <p className="text-gray-600 text-sm">
                      Custom software, ERP implementations, cloud migrations, and IT infrastructure services
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-nstu-green-800 rounded-xl text-white">
                <h3 className="font-heading text-xl font-bold mb-4">Quick Contact</h3>
                <a 
                  href="tel:+919541377777"
                  className="flex items-center gap-3 text-nstu-gold-400 hover:text-nstu-gold-300 text-2xl font-bold transition-colors"
                  data-testid="quick-contact-phone"
                >
                  <Phone className="w-8 h-8" />
                  +91 95413 77777
                </a>
                <p className="text-gray-300 mt-2 text-sm">
                  Speak directly with our team for urgent requirements
                </p>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-nstu-green-800 mb-4">
                        Thank You!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Your message has been received. Our team will review your inquiry and get 
                        back to you within 24 hours.
                      </p>
                      <Button 
                        onClick={() => setSubmitted(false)}
                        className="bg-nstu-green-800 text-white hover:bg-nstu-green-700"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-testid="contact-form">
                      <h3 className="font-heading text-2xl font-bold text-nstu-green-800 mb-6">
                        Send Us a Message
                      </h3>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label htmlFor="name" className="text-gray-700 mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            className="border-gray-300 focus:border-nstu-green-800 focus:ring-nstu-green-800"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-700 mb-2 block">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            className="border-gray-300 focus:border-nstu-green-800 focus:ring-nstu-green-800"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label htmlFor="phone" className="text-gray-700 mb-2 block">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 XXXXX XXXXX"
                            className="border-gray-300 focus:border-nstu-green-800 focus:ring-nstu-green-800"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="subject" className="text-gray-700 mb-2 block">
                            Subject *
                          </Label>
                          <select
                            id="subject"
                            name="subject"
                            required
                            value={formData.subject}
                            onChange={handleChange}
                            className="w-full h-10 px-3 rounded-md border border-gray-300 focus:border-nstu-green-800 focus:ring-nstu-green-800 focus:outline-none bg-white text-gray-900"
                            data-testid="contact-subject-select"
                          >
                            <option value="">Select a subject</option>
                            {subjectOptions.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mb-6">
                        <Label htmlFor="message" className="text-gray-700 mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your project or inquiry..."
                          rows={5}
                          className="border-gray-300 focus:border-nstu-green-800 focus:ring-nstu-green-800 resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={loading}
                        className="w-full bg-nstu-green-800 text-white hover:bg-nstu-green-700 py-6 text-lg font-semibold btn-lift"
                        data-testid="contact-submit-btn"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <Send className="w-5 h-5" />
                            Send Message
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map / Office Info Section */}
      <section className="py-20 bg-white" data-testid="office-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-nstu-gold-100 text-nstu-gold-700 mb-4">Our Presence</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-nstu-green-800 mb-4">
              Pan-India Operations
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              With our headquarters in New Delhi and regional offices across India, we ensure 
              local support and seamless project delivery nationwide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { city: 'Bhiwani (HQ)', type: 'Corporate Office', address: 'VPO Haluwas, 127021' },
              { city: 'Kurukshetra', type: 'Regional Office', address: 'Thanesar' },
              { city: 'Pehowa', type: 'Project Office', address: 'Kurukshetra District' }
            ].map((office) => (
              <Card key={office.city} className="text-center card-hover border-0 shadow-lg">
                <CardContent className="p-6">
                  <MapPin className="w-10 h-10 text-nstu-gold-500 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-bold text-nstu-green-800 mb-1">
                    {office.city}
                  </h3>
                  <p className="text-nstu-gold-600 font-medium mb-1">{office.type}</p>
                  <p className="text-gray-500 text-sm">{office.address}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-nstu-green-800" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Prefer a Direct Conversation?
          </h2>
          <p className="text-gray-300 mb-8">
            Call us directly to discuss your requirements with our team
          </p>
          <a href="tel:+919541377777">
            <Button size="lg" className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-bold btn-lift" data-testid="cta-phone-btn">
              <Phone className="w-5 h-5 mr-2" />
              Call +91 95413 77777
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
